import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarCheck2, CheckCircle2, Download, FileSignature, FileText, FolderOpen, Loader2, ShieldCheck, UploadCloud } from 'lucide-react';
import {
  fetchDocuments,
  fetchDocumentRequests,
  uploadDocument,
  downloadInvoicePdf,
  fetchConvocations,
  downloadConvocationPdf,
} from '../api/candidate';
import { formatEuro, statusBadge } from '../lib/format';
import dayjs from '../lib/dates';
import { awaitsStudent, type CandidateDocumentRequest, type CandidateInvoice } from '../types';
import PendingSignatures from '../components/PendingSignatures';
import { Badge, type BadgeTone, Button, Card, EmptyState, cx } from '../components/charte';

// Mes documents (refonte du 14/10/2026) : pièces demandées, convocations, factures et contrats — mêmes
// données, mêmes envois. Les pièces à fournir sont en tête ; sur téléphone, les boutons passent sous la ligne.

const ACCEPTED_UPLOAD = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Couleur de pastille de la charte pour un statut de facture ou de contrat (libellés de lib/format). */
function statusTone(status: string): BadgeTone {
  if (status === 'paid' || status === 'signed') return 'ok';
  if (status === 'unpaid' || status === 'pending_signature') return 'warn';
  if (status === 'sent') return 'todo';
  return 'mute';
}

function StatusBadge({ status }: { status: string }) {
  return <Badge tone={statusTone(status)}>{statusBadge(status).label}</Badge>;
}

/**
 * Statut d'une facture : son état de règlement calculé par l'auto-école (F39 : réglée, à régler, en
 * retard), sinon son statut enregistré. Mêmes libellés que l'écran de l'auto-école.
 */
function InvoiceBadge({ invoice }: { invoice: CandidateInvoice }) {
  const s = invoice.settlement;
  if (!s || invoice.status === 'paid') return <StatusBadge status={invoice.status} />;
  const [label, tone]: [string, BadgeTone] =
    s.state === 'paid'
      ? ['Réglée', 'ok']
      : s.state === 'credited'
        ? ['Annulée par avoir', 'mute']
        : s.state === 'partial'
          ? [s.overdue ? 'Partiellement réglée · en retard' : 'Partiellement réglée', s.overdue ? 'warn' : 'todo']
          : [s.overdue ? 'En retard' : 'À régler', s.overdue ? 'warn' : 'todo'];
  return <Badge tone={tone}>{label}</Badge>;
}

function Row({ icon, tone = 'indigo', title, sub, children }: {
  icon: React.ReactNode;
  tone?: 'indigo' | 'ok' | 'warn';
  title: React.ReactNode;
  sub?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3.5 first:pt-0 last:pb-0">
      <span
        className={cx(
          'grid h-10 w-10 shrink-0 place-items-center rounded-xl',
          tone === 'ok' ? 'bg-success-light text-success' : tone === 'warn' ? 'bg-warn-bg text-warn-fg' : 'bg-surface-indigo text-primary',
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-heading">{title}</p>
        {sub && <div className="text-xs text-secondary">{sub}</div>}
      </div>
      {children && <div className="flex shrink-0 items-center gap-2 max-sm:basis-full max-sm:pl-[52px]">{children}</div>}
    </li>
  );
}

function RequestRow({ req }: { req: CandidateDocumentRequest }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadDocument(req.id, file),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['candidate', 'document-requests'] });
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message?: unknown }).message) : "Échec de l'envoi.";
      setError(msg);
    },
  });

  // Envoyée (en cours de vérification) ou validée : réglée côté élève ; refusée : à renvoyer.
  const provided = req.status === 'provided';
  const validated = req.status === 'validated';
  const rejected = req.status === 'rejected';

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Fichier trop volumineux (max 10 Mo).');
      return;
    }
    upload.mutate(file);
  };

  return (
    <>
      <Row
        tone={provided || validated ? 'ok' : 'warn'}
        icon={
          validated ? (
            <ShieldCheck size={18} aria-hidden />
          ) : provided ? (
            <CheckCircle2 size={18} aria-hidden />
          ) : rejected ? (
            <AlertTriangle size={18} aria-hidden />
          ) : (
            <FileText size={18} aria-hidden />
          )
        }
        title={req.label}
        sub={
          <>
            {req.note ? req.note : `Demandé le ${dayjs(req.requestedAt).format('DD/MM/YYYY')}`}
            {provided && req.fileName && <span className="mt-0.5 block truncate text-success-dark">✓ Envoyé : {req.fileName} · en cours de vérification</span>}
            {validated && <span className="mt-0.5 block text-success-dark">✓ Validé par votre auto-école</span>}
            {rejected && (
              <span className="mt-0.5 block text-danger-dark">
                {req.rejectionReason ? `Refusé : ${req.rejectionReason}` : 'Refusé : envoyez une nouvelle pièce.'}
              </span>
            )}
          </>
        }
      >
        {validated ? (
          <Badge tone="ok">Validé</Badge>
        ) : (
          <>
            <input ref={inputRef} type="file" accept={ACCEPTED_UPLOAD} className="hidden" onChange={onPick} />
            <Button
              size="sm"
              variant={provided ? 'secondary' : 'soft'}
              icon={UploadCloud}
              loading={upload.isPending}
              onClick={() => inputRef.current?.click()}
              className="max-sm:w-full"
            >
              {provided ? 'Remplacer' : rejected ? 'Renvoyer le document' : 'Envoyer le document'}
            </Button>
          </>
        )}
      </Row>
      {error && (
        <li role="alert" className="-mt-1 pb-3 pl-[52px] text-xs text-danger-dark">
          {error}
        </li>
      )}
    </>
  );
}

function RequestedDocuments() {
  const { data, isLoading } = useQuery({ queryKey: ['candidate', 'document-requests'], queryFn: fetchDocumentRequests });

  const requests = data ?? [];
  if (isLoading || requests.length === 0) return null;

  const pending = requests.filter(awaitsStudent);

  return (
    <Card className={cx(pending.length > 0 && 'ring-1 ring-warn-line/50')}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-title-sm text-heading">Documents demandés</h2>
        {pending.length > 0 && <Badge tone="warn">{pending.length} à fournir</Badge>}
      </div>
      <p className="mt-1 text-sm text-secondary">
        {pending.length > 0
          ? `Votre auto-école attend ${pending.length} document${pending.length > 1 ? 's' : ''}. Envoyez une photo ou un PDF (max 10 Mo).`
          : 'Tous les documents demandés ont été transmis.'}
      </p>
      <ul className="mt-4 divide-y divide-slate-100">
        {requests.map((req) => (
          <RequestRow key={req.id} req={req} />
        ))}
      </ul>
    </Card>
  );
}

/** Récapitulatifs de convocation à l'examen envoyés par l'auto-école (les plus récents d'abord). */
function ExamConvocations() {
  const { data } = useQuery({ queryKey: ['candidate', 'convocations'], queryFn: fetchConvocations });
  const [failed, setFailed] = useState(false);
  const convocations = data ?? [];
  if (convocations.length === 0) return null;
  return (
    <Card>
      <h2 className="text-title-sm text-heading">Convocations aux examens</h2>
      <p className="mt-1 text-sm text-secondary">
        Récapitulatifs envoyés par votre auto-école. La convocation officielle vous est envoyée par e-mail par RdvPermis :
        elle fait foi.
      </p>
      {failed && (
        <p role="alert" className="mt-3 rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
          Le téléchargement a échoué. Réessayez.
        </p>
      )}
      <ul className="mt-4 divide-y divide-slate-100">
        {convocations.map((c) => (
          <Row
            key={c.id}
            icon={<CalendarCheck2 size={18} aria-hidden />}
            title={`Examen du ${dayjs(c.examStartsAt).format('DD/MM/YYYY à HH:mm')}`}
            sub={<span className="block truncate">{c.center} · reçu le {dayjs(c.sentAt).format('DD/MM/YYYY')}</span>}
          >
            <Button
              size="sm"
              icon={Download}
              onClick={() => {
                setFailed(false);
                downloadConvocationPdf(c).catch(() => setFailed(true));
              }}
              aria-label={`Télécharger la convocation de l’examen du ${dayjs(c.examStartsAt).format('DD/MM/YYYY')}`}
            >
              PDF
            </Button>
          </Row>
        ))}
      </ul>
    </Card>
  );
}

export default function DocumentsView() {
  const { data, isLoading } = useQuery({ queryKey: ['candidate', 'documents'], queryFn: fetchDocuments });

  const invoices = data?.invoices ?? [];
  const contracts = data?.contracts ?? [];
  const empty = !isLoading && invoices.length === 0 && contracts.length === 0;

  return (
    <div className="grid gap-5 sm:gap-6">
      <div>
        <h1 className="text-title-md text-heading sm:text-title-lg">Mes documents</h1>
        <p className="mt-1 text-sm text-secondary">Vos factures, contrats de formation et convocations aux examens.</p>
      </div>

      <PendingSignatures />

      <RequestedDocuments />

      <ExamConvocations />

      {isLoading ? (
        <Card className="flex items-center text-sm text-secondary">
          <Loader2 className="mr-2 animate-spin" size={16} /> Chargement…
        </Card>
      ) : empty ? (
        <Card>
          <EmptyState icon={FolderOpen} title="Aucun document" description="Aucun document disponible pour le moment." />
        </Card>
      ) : (
        <div className="grid gap-5 sm:gap-6 xl:grid-cols-2 xl:items-start">
          {/* Factures */}
          <Card>
            <h2 className="text-title-sm text-heading">Factures</h2>
            {invoices.length === 0 ? (
              <p className="mt-3 text-sm text-secondary">Aucune facture.</p>
            ) : (
              <ul className="mt-4 divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <Row
                    key={inv.id}
                    icon={<FileText size={18} aria-hidden />}
                    title={inv.title}
                    sub={
                      <>
                        {inv.number} · {dayjs(inv.issueDate).format('DD/MM/YYYY')} ·{' '}
                        <span className="font-semibold text-heading">{formatEuro(inv.totalTtc)}</span>
                      </>
                    }
                  >
                    <InvoiceBadge invoice={inv} />
                    <Button size="sm" icon={Download} title="Télécharger la facture" onClick={() => downloadInvoicePdf(inv.id, inv.number)}>
                      PDF
                    </Button>
                  </Row>
                ))}
              </ul>
            )}
          </Card>

          {/* Contrats */}
          <Card>
            <h2 className="text-title-sm text-heading">Contrats</h2>
            {contracts.length === 0 ? (
              <p className="mt-3 text-sm text-secondary">Aucun contrat.</p>
            ) : (
              <ul className="mt-4 divide-y divide-slate-100">
                {contracts.map((c) => (
                  <Row
                    key={c.id}
                    icon={<FileSignature size={18} aria-hidden />}
                    title={`Contrat ${c.number}`}
                    sub={c.signedAt ? `Signé le ${dayjs(c.signedAt).format('DD/MM/YYYY')}` : 'Non signé'}
                  >
                    <StatusBadge status={c.status} />
                  </Row>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
